import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import * as api from '../services/api';
import { ClassContentPicker } from '../components/ClassContentPicker';
import { useAuth } from '../context/AuthContext';
import type { SchoolClass } from '../types/class';
import type { ClassContentSelection } from '../types/classActivityAssignment';
import {
  EMPTY_CLASS_CONTENT_SELECTION,
  selectionFromAssignments
} from '../types/classActivityAssignment';

export function ClassContentPage() {
  const { user, isSuperAdmin } = useAuth();
  const [searchParams] = useSearchParams();
  const schoolId = user?.schoolId || searchParams.get('schoolId') || null;

  const [classes, setClasses] = useState<SchoolClass[]>([]);
  const [selectedClassId, setSelectedClassId] = useState('');
  const [selection, setSelection] = useState<ClassContentSelection>(EMPTY_CLASS_CONTENT_SELECTION);
  const [loadingClasses, setLoadingClasses] = useState(true);
  const [loadingAssignments, setLoadingAssignments] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const loadClasses = useCallback(async () => {
    if (!schoolId) return;
    setLoadingClasses(true);
    setError(null);
    try {
      const items = await api.listClasses(schoolId, { activeOnly: true });
      setClasses(items);
      setSelectedClassId((current) => {
        if (current && items.some((item) => item.id === current)) return current;
        return items[0]?.id ?? '';
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load classes');
    } finally {
      setLoadingClasses(false);
    }
  }, [schoolId]);

  useEffect(() => {
    void loadClasses();
  }, [loadClasses]);

  useEffect(() => {
    if (!selectedClassId) {
      setSelection(EMPTY_CLASS_CONTENT_SELECTION);
      return;
    }

    let cancelled = false;

    async function loadAssignments() {
      setLoadingAssignments(true);
      setError(null);
      setSuccess(null);
      try {
        const assignments = await api.listClassActivityAssignments({
          classId: selectedClassId
        });
        if (!cancelled) {
          setSelection(selectionFromAssignments(assignments));
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load assigned content');
          setSelection(EMPTY_CLASS_CONTENT_SELECTION);
        }
      } finally {
        if (!cancelled) setLoadingAssignments(false);
      }
    }

    void loadAssignments();
    return () => {
      cancelled = true;
    };
  }, [selectedClassId]);

  async function handleSave() {
    if (!selectedClassId) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await api.replaceClassActivityAssignments(selectedClassId, selection);
      const className = classes.find((item) => item.id === selectedClassId)?.name ?? 'Class';
      setSuccess(`Content updated for ${className}.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save content');
    } finally {
      setSaving(false);
    }
  }

  if (!schoolId) {
    return (
      <section className="admin-panel">
        <div className="panel-heading">
          <div>
            <h2>Content</h2>
            <p>
              {isSuperAdmin
                ? 'Open a school from Schools and pass schoolId, or sign in as a school admin.'
                : 'Your account is not linked to a school yet.'}
            </p>
          </div>
        </div>
      </section>
    );
  }

  const selectedClass = classes.find((item) => item.id === selectedClassId) ?? null;

  return (
    <section className="admin-panel class-content-page">
      <div className="panel-heading">
        <div>
          <h2>Content</h2>
          <p>
            Choose which character pillars, missions, learning lectures, and home activities are
            available for each class.
          </p>
        </div>
      </div>

      {error ? (
        <p className="form-error" role="alert">
          {error}
        </p>
      ) : null}
      {success ? (
        <p className="form-success" role="status">
          {success}
        </p>
      ) : null}

      {loadingClasses ? <p className="muted">Loading classes…</p> : null}

      {!loadingClasses && classes.length === 0 ? (
        <p className="muted">No active classes yet. Create a class first under Classes.</p>
      ) : null}

      {!loadingClasses && classes.length > 0 ? (
        <div className="class-content-page-layout">
          <label className="class-content-class-select">
            Class
            <select
              value={selectedClassId}
              onChange={(e) => setSelectedClassId(e.target.value)}
              disabled={saving}
            >
              {classes.map((classDoc) => (
                <option key={classDoc.id} value={classDoc.id}>
                  {classDoc.name}
                  {classDoc.grade ? ` · ${classDoc.grade}` : ''}
                </option>
              ))}
            </select>
          </label>

          <div className="class-content-page-card">
            <div className="class-content-page-card-header">
              <div>
                <h3>{selectedClass?.name ?? 'Class content'}</h3>
                <p className="muted">
                  Select pillars first, then missions, lectures, and home activities under each.
                </p>
              </div>
              <button
                type="button"
                className="primary-btn"
                disabled={saving || loadingAssignments || !selectedClassId}
                onClick={() => void handleSave()}
              >
                {saving ? 'Saving…' : 'Save content'}
              </button>
            </div>

            {loadingAssignments ? (
              <p className="muted">Loading assigned content…</p>
            ) : (
              <ClassContentPicker
                value={selection}
                onChange={setSelection}
                disabled={saving}
              />
            )}
          </div>
        </div>
      ) : null}
    </section>
  );
}
